import { useCallback, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useQuotincLink } from '@/features/quotinc/quotinc.api';
import { ONBOARDING_ME_KEY, onboardingApi } from './onboarding.api';

/**
 * The Quotinc link in onboarding (2026-10-29). Owner: "During onboarding of a
 * tenant who has access to both platforms, show how to click and access the
 * other platform."
 *
 * On a LINKED install (`GET /api/quotinc/link` says on) the welcome's last
 * step gains one line, the tour a stop on the sidebar's Quotinc entry
 * (`data-tour="quotinc"`), and the getting-started list an optional "Open
 * Quotinc" line (sent by the API) that ticks the first time the person opens
 * Quotinc from Amassa. An unlinked install (Asian Trails) shows none of it.
 */

/** `data-tour` of the sidebar's Quotinc entry, and the checklist line's id (the API's `QuotincItemId`). */
export const QUOTINC_TOUR_TARGET = 'quotinc';
export const QUOTINC_CHECKLIST_ID = 'quotinc';

/** The welcome's line: where the other app lives. */
export const QUOTINC_WELCOME_LINE =
  'You can open Quotinc from here too: its entry sits at the foot of the sidebar, just above your name.';

/**
 * Records that the person opened Quotinc (the API keeps the first time only),
 * then refreshes the checklist so "Open Quotinc" ticks. Fire and forget: a
 * failure (an agent, whom the API refuses; an unlinked install; a blip)
 * never stops the link from opening.
 */
export function useMarkQuotincOpened(): () => void {
  const qc = useQueryClient();
  return useCallback(() => {
    void onboardingApi
      .quotincOpened()
      .catch(() => undefined)
      .then(() => qc.invalidateQueries({ queryKey: ONBOARDING_ME_KEY }));
  }, [qc]);
}

/** The welcome's "Start" step: one line, only while the install is linked. */
export function QuotincWelcomeLine() {
  const link = useQuotincLink();
  if (!link.on) return null;
  return <p className="onb-hint onb-quotinc-line">{QUOTINC_WELCOME_LINE}</p>;
}

/**
 * The checklist's "Open Quotinc" line opens Quotinc itself, in a new tab
 * (Quotinc keeps its own sign-in), and ticks. Without a usable link it falls
 * back to what the caller renders (the Help section's internal link).
 */
export function QuotincChecklistLink({ className, children, fallback }: { className?: string; children: ReactNode; fallback: ReactNode }) {
  const link = useQuotincLink();
  const mark = useMarkQuotincOpened();
  if (!link.on || !link.appUrl) return <>{fallback}</>;
  return (
    <a href={link.appUrl} target="_blank" rel="noopener noreferrer" className={className} onClick={mark}>
      {children}
      <span className="sr-only"> (opens in a new tab)</span>
    </a>
  );
}
